import { defaults } from './utils.js';
import request from './request.js';
import { View as viewContext } from '@twintag/twintag-core';
import { setAdminHost, setHost, View } from '@esoptra/zaza-sdk';
import { PRODUCTIONFLAG, ADMINHOST, HOST } from '../settings';

const getDefaults = () => {
    return {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        addPath: '/locales/add/{{lng}}/{{ns}}',
        allowMultiLoading: false,
        parse: (data) => JSON.parse(data),
        stringify: JSON.stringify,
        parsePayload: (namespace, key, fallbackValue) => ({
            [key]: fallbackValue || '',
        }),
        request,
        reloadInterval: typeof window !== 'undefined' ? false : 60 * 60 * 1000,
        customHeaders: {},
        queryStringParams: {},
        crossDomain: false, // used for XmlHttpRequest
        withCredentials: false, // used for XmlHttpRequest
        overrideMimeType: false, // used for XmlHttpRequest
        requestOptions: {
            // used for fetch
            mode: 'cors',
            credentials: 'same-origin',
            cache: 'default',
        },
        defaultLanguage: 'en',
    };
};

class Backend {
    constructor(services, options = {}, allOptions = {}) {
        this.services = services;
        this.options = options;
        this.allOptions = allOptions;
        this.type = 'backend';
        this.init(services, options, allOptions);
        this.view = undefined;
        this.translationObj = undefined;
        this.defaultLanguage = '';
    }

    init(services, options = {}, allOptions = {}) {
        this.services = services;
        this.options = defaults(options, this.options || {}, getDefaults());
        this.allOptions = allOptions;
        if (this.services && this.options.reloadInterval) {
            setInterval(() => this.reload(), this.options.reloadInterval);
        }
    }

    readMulti(languages, namespaces, callback) {
        this.loadNewURL(callback, languages, namespaces);
    }

    async read(language, namespace, callback) {
        if (!this.view) {
            if (!PRODUCTIONFLAG) {
                setHost(HOST);
                setAdminHost(ADMINHOST);
            }
            this.view = new View(viewContext.getId());
        }
        this.loadNewURL(callback, language, namespace);
    }

    async loadNewURL(callback, languages, namespaces) {
        if (
            this.options.backendOptions &&
            this.options.backendOptions.length > 0
        ) {
            this.defaultLanguage = this.options.backendOptions[0]
                .defaultProjectLanguage
                ? this.options.backendOptions[0].defaultProjectLanguage
                : '';
        }
        if (!this.translationObj) {
            this.translationObj = await this.view
                .object('$translation')
                .catch((err) => {
                    return callback('failed to get translation', false);
                });
        }
        let objData;
        if (this.translationObj) {
            objData = await this.translationObj
                .get('', languages)
                .catch((err) => {
                    return callback('failed to get translation', false);
                });
        }
        let ret, parseErr;
        try {
            let langData = {};
            if (objData) {
                objData.forEach((item) => {
                    if (
                        (this.defaultLanguage === '' ||
                            this.defaultLanguage === languages) &&
                        item['value']
                    ) {
                        langData[item['key']] = item['value'];
                    } else if (item[`value$${languages}`] !== null) {
                        langData[item['key']] = item[`value$${languages}`];
                    }
                });
            }
            ret = langData;
        } catch (e) {
            parseErr = 'failed parsing response to json';
        }
        if (parseErr) return callback(parseErr, false);
        callback(null, ret);
    }

    create(languages, namespace, key, fallbackValue, callback) {
        // If there is a falsey addPath, then abort -- this has been disabled.
        if (!this.options.addPath) return;
        if (typeof languages === 'string') languages = [languages];
        const payload = this.options.parsePayload(
            namespace,
            key,
            fallbackValue
        );
        let finished = 0;
        const dataArray = [];
        const resArray = [];
        languages.forEach((lng) => {
            let addPath = this.options.addPath;
            if (typeof this.options.addPath === 'function') {
                addPath = this.options.addPath(lng, namespace);
            }
            const url = this.services.interpolator.interpolate(addPath, {
                lng: lng,
                ns: namespace,
            });

            this.options.request(this.options, url, payload, (data, res) => {
                // TODO: if res.status === 4xx do log
                finished += 1;
                dataArray.push(data);
                resArray.push(res);
                if (finished === languages.length) {
                    if (callback) callback(dataArray, resArray);
                }
            });
        });
    }

    reload() {
        const { backendConnector, languageUtils, logger } = this.services;
        const currentLanguage = backendConnector.language;
        if (currentLanguage && currentLanguage.toLowerCase() === 'cimode')
            return; // avoid loading resources for cimode

        const toLoad = [];
        const append = (lng) => {
            const lngs = languageUtils.toResolveHierarchy(lng);
            lngs.forEach((l) => {
                if (toLoad.indexOf(l) < 0) toLoad.push(l);
            });
        };

        append(currentLanguage);

        if (this.allOptions.preload)
            this.allOptions.preload.forEach((l) => append(l));

        toLoad.forEach((lng) => {
            this.allOptions.ns.forEach((ns) => {
                backendConnector.read(
                    lng,
                    ns,
                    'read',
                    null,
                    null,
                    (err, data) => {
                        if (err)
                            logger.warn(
                                `loading namespace ${ns} for language ${lng} failed`,
                                err
                            );
                        if (!err && data)
                            logger.log(
                                `loaded namespace ${ns} for language ${lng}`,
                                data
                            );

                        backendConnector.loaded(`${lng}|${ns}`, err, data);
                    }
                );
            });
        });
    }

    async getURL(view, fileName) {
        if (this.epsilonURL) {
            return this.epsilonURL;
        }
        let result = await view.list();
        const virtualFile = result?.filter(function (result) {
            return result.Name === fileName;
        });
        if (virtualFile && virtualFile[0]) {
            this.epsilonURL =
                window.origin +
                '/api/v1/views/' +
                virtualFile[0].TemplateBagId +
                '/files/' +
                virtualFile[0].FileQid;
            return this.epsilonURL;
        }
        return '';
    }
}

Backend.type = 'backend';

export default Backend;
